import "./carousel.scss";
import {
    type FC,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";

interface Props {
    changeTime?: number;
    children: Array<ReactNode>;
}

type Direction = "right" | "left";

export const Carousel: FC<Props> = ({ changeTime, children }) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [ current, setCurrent ] = useState(0);

    const [ direction, setDirection ] = useState<Direction | null>(null);
    const [ nextIndex, setNextIndex ] = useState<null | number>(null);

    const onChange = (next: number): void => {
        if(current === next) {
            return;
        }

        const direction = current > next ? "right" : "left";

        const length = children.length;

        let index = next % length;

        if(index < 0) {
            index = length + index;
        }

        if(current === index) {
            return;
        }

        setNextIndex(index);
        setDirection(direction);
    };

    useEffect(() => {
        if(typeof changeTime !== "number") {
            return;
        }

        const timeout = setTimeout(() => {
            onChange(current + 1);
        }, changeTime);

        timeoutRef.current = timeout;

        return () => {
            clearTimeout(timeout);
        };
        // Note: onChange is intentionally not added
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ changeTime, current ]);

    const selected = typeof nextIndex === "number" ? nextIndex : current;

    return (
        <div className="h-full w-full overflow-hidden relative">
            <div className={"h-full" + (typeof nextIndex === "number" ? " hidden" : "")}>
                {children[current]}
            </div>
            {typeof nextIndex === "number" && (
                <div
                    onAnimationEnd={() => {
                        if(typeof nextIndex !== "number") {
                            return;
                        }

                        setCurrent(nextIndex);
                        setDirection(null);
                        setNextIndex(null);
                    }}
                    className={
                        "w-full h-full grid grid-cols-[100%_100%] " +
                        (direction === "left" ? "swipe-left" : "swipe-right")
                    }
                >
                    {direction === "right" && (
                        children[nextIndex]
                    )}
                    {children[current]}
                    {direction === "left" && (
                        children[nextIndex]
                    )}
                </div>
            )}
            <div
                className="absolute bottom-0 grid px-4 w-full gap-2 box-border"
                style={{
                    gridTemplateColumns: `repeat(${children.length}, 1fr)`,
                }}
            >
                {children.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            onChange(index);
                        }}
                        className={
                            "h-1 transition-all duration-300 cursor-pointer " +
                            "hover:scale-y-[2.5] " + (selected === index ? "bg-blue-700" : "bg-white")
                        }
                    >
                        <span className="sr-only">Slide ${index + 1}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};
